import Button from '@atlaskit/button/new';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import OpenIcon from '@atlaskit/icon/glyph/open';
import Popup from '@atlaskit/popup';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useState } from 'react';

import { settingsCodeReviewerPath } from '../../../routes/paths';
import { useAnalyticsWebClient } from '../../../utils/analytics/context';
import {
	getDefaultBbcWorkspacesPage,
	handleRedirectForWorkspaces,
	useBibucketConnectedWorkspacesList,
	WorkspacesModalContent,
} from '../utils/bitbucket-workspaces';

import { WorkspacesModalDialog } from './bbc-workspaces-modal';

const primaryActionStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'border.radius.200',
});

export const CodeReviewerSettings = ({
	showBitbucket,
	showGitHub,
}: {
	showBitbucket: boolean;
	showGitHub: boolean;
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const { workspacesList, isLoading } = useBibucketConnectedWorkspacesList(showBitbucket);
	const [isWorkspacesModalOpen, setIsWorkspacesModalOpen] = useState<boolean>(false);
	const bitbucketRedirectUrls = handleRedirectForWorkspaces(workspacesList);
	const analyticsWebClient = useAnalyticsWebClient();

	const getWorkspaceLink = () => {
		if (!isLoading) {
			if (bitbucketRedirectUrls.length === 1) {
				return bitbucketRedirectUrls.at(0)?.redirectUrl;
			}

			if (bitbucketRedirectUrls.length === 0) {
				return getDefaultBbcWorkspacesPage();
			}
		}

		return undefined;
	};

	return (
		<>
			{isWorkspacesModalOpen && (
				<WorkspacesModalDialog
					closeModalDialog={() => setIsWorkspacesModalOpen(!isWorkspacesModalOpen)}
					title={WorkspacesModalContent.title}
					infoPanelDetails={WorkspacesModalContent.infoPanelDetails}
					items={bitbucketRedirectUrls} // if a modal is being shown, it would have workspaces
				/>
			)}
			<Box>
				<Stack space="space.100">
					<Popup
						isOpen={isOpen}
						onClose={() => setIsOpen(false)}
						placement="bottom-start"
						content={() => (
							<DropdownItemGroup>
								{showBitbucket && (
									<DropdownItem
										isDisabled={isLoading}
										elemBefore={<OpenIcon label="" />}
										description="Configure Code Reviewer for Bitbucket"
										href={getWorkspaceLink()}
										onClick={() => {
											if (bitbucketRedirectUrls.length > 1) {
												setIsWorkspacesModalOpen(!isWorkspacesModalOpen);
											}
											analyticsWebClient.sendUIEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'code-reviewer-bbc-settings',
												source: 'home-page-agents',
											});
										}}
										target="_blank"
										testId="code-reviewer-bbc-settings-button"
									>
										Bitbucket
									</DropdownItem>
								)}
								{showGitHub && (
									<DropdownItem
										elemBefore={<OpenIcon label="" />}
										description="Configure Code Reviewer for GitHub"
										href={settingsCodeReviewerPath}
										onClick={() => {
											analyticsWebClient.sendUIEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'code-reviewer-gh-settings',
												source: 'home-page-agents',
											});
										}}
										testId="code-reviewer-github-settings-button"
									>
										GitHub
									</DropdownItem>
								)}
							</DropdownItemGroup>
						)}
						trigger={(triggerProps) => (
							<Box xcss={primaryActionStyles}>
								<Button
									{...triggerProps}
									appearance="subtle"
									iconAfter={() => <ChevronDownIcon label="" />}
									onClick={() => setIsOpen(!isOpen)}
									spacing="compact"
									testId="enable-code-reviewer"
								>
									Enable Code Reviewer on repositories
								</Button>
							</Box>
						)}
					/>
				</Stack>
			</Box>
		</>
	);
};
